import { InterpolationError } from './InterpolationError';
import type { Expression, Literal, PathSegment, Template } from './types';

/**
 * Parse a Configurable Experiences template string into a deterministic AST.
 *
 * A template is any string that may contain zero or more `{{expression}}`
 * runs. Each `{{…}}` becomes an {@link Expression} node; everything else
 * becomes a {@link Literal} node. Expression bodies are pre-lexed into
 * {@link PathSegment}s so `getByPath()` can resolve them without re-parsing.
 *
 * Two expression shapes are recognized:
 *
 * - **Path** (e.g. `context.tenantId`, `results.programs[*].organizationId`,
 *   `topology.siblings.block-a.label`, `topology.siblings["block a"].label`):
 *   dotted identifier path. Identifiers may contain `-`. A `["..."]` or
 *   `['...']` key selects a property whose name is not an identifier.
 *   An optional `[*]` wildcard selector flattens one level of an array.
 * - **Function call** (e.g. `comparison.isSelected(item.offerId)`): captured
 *   as a structured `kind: 'functionCall'` node. **DB-1.2 parses these but
 *   `interpolate()` resolves them to `undefined`** — DB-2.2
 *   (ComparisonProvider) owns runtime resolution per ADR-12.
 *
 * The parser is strict about structural syntax (throws {@link
 * InterpolationError} on unclosed `{{`, empty `{{}}`, nested `{{{{`, or
 * invalid identifiers) because publish-time validators (ADR-8, §12) are
 * expected to surface these loudly. Runtime resolution failures never throw
 * — missing paths resolve to `undefined` downstream.
 *
 * @example
 * parse('Hello {{context.name}}!');
 * // => { segments: [
 * //   { type: 'literal', value: 'Hello ' },
 * //   { type: 'expression', kind: 'path', path: 'context.name',
 * //     raw: 'context.name',
 * //     segments: [{ type: 'key', name: 'context' }, { type: 'key', name: 'name' }] },
 * //   { type: 'literal', value: '!' },
 * // ] }
 *
 * @example
 * parse('{{results.programs[*].organizationId}}');
 * // => one Expression with segments
 * //    [{ key: 'results' }, { key: 'programs' }, { wildcard }, { key: 'organizationId' }]
 */
export function parse(template: string): Template {
  const segments: Array<Literal | Expression> = [];
  const len = template.length;
  let i = 0;
  let literalStart = 0;

  while (i < len) {
    const isOpen =
      template.charCodeAt(i) === 0x7b /* { */ &&
      i + 1 < len &&
      template.charCodeAt(i + 1) === 0x7b;

    if (!isOpen) {
      i++;
      continue;
    }

    if (i > literalStart) {
      segments.push({
        type: 'literal',
        value: template.slice(literalStart, i),
      });
    }

    const exprStart = i + 2;
    let j = exprStart;
    let closed = false;

    while (j < len) {
      const quote = template[j];
      if (quote === '"' || quote === "'") {
        const afterQuote = skipQuoted(template, j + 1, quote);
        if (afterQuote === undefined) break;
        j = afterQuote;
        continue;
      }
      if (
        j + 1 < len &&
        template.charCodeAt(j) === 0x7b &&
        template.charCodeAt(j + 1) === 0x7b
      ) {
        throw new InterpolationError(
          'Nested "{{" inside expression',
          template,
          j,
        );
      }
      if (
        j + 1 < len &&
        template.charCodeAt(j) === 0x7d /* } */ &&
        template.charCodeAt(j + 1) === 0x7d
      ) {
        closed = true;
        break;
      }
      j++;
    }

    if (!closed) {
      throw new InterpolationError('Unclosed "{{"', template, i);
    }

    const raw = template.slice(exprStart, j);
    segments.push(parseExpression(raw, template, exprStart));

    i = j + 2;
    literalStart = i;
  }

  if (literalStart < len) {
    segments.push({
      type: 'literal',
      value: template.slice(literalStart),
    });
  }

  return { segments };
}

/**
 * Parse the inside of a `{{…}}` run. Internal helper — not exported.
 */
function parseExpression(
  raw: string,
  template: string,
  offset: number,
): Expression {
  const trimmed = raw.trim();
  const trimmedOffset =
    trimmed.length === 0 ? offset : offset + raw.indexOf(trimmed);
  if (trimmed.length === 0) {
    throw new InterpolationError('Empty expression "{{}}"', template, offset);
  }

  const parenIdx = indexOutsideQuotes(trimmed, '(');
  if (parenIdx !== -1) {
    return parseFunctionCall(trimmed, template, trimmedOffset, parenIdx);
  }

  const { path, segments } = parsePath(trimmed, template, trimmedOffset);
  return {
    type: 'expression',
    raw: trimmed,
    path,
    kind: 'path',
    segments,
  };
}

function parseFunctionCall(
  trimmed: string,
  template: string,
  offset: number,
  parenIdx: number,
): Expression {
  if (trimmed.charCodeAt(trimmed.length - 1) !== 0x29 /* ) */) {
    throw new InterpolationError(
      'Unterminated function call — expected ")"',
      template,
      offset + trimmed.length,
    );
  }

  const calleeSlice = trimmed.slice(0, parenIdx);
  const calleeLeading = calleeSlice.length - calleeSlice.trimStart().length;
  const calleeText = calleeSlice.trim();

  const argSlice = trimmed.slice(parenIdx + 1, trimmed.length - 1);
  const argLeading = argSlice.length - argSlice.trimStart().length;
  const argText = argSlice.trim();

  if (calleeText.length === 0) {
    throw new InterpolationError(
      'Function call has no callee',
      template,
      offset,
    );
  }

  const calleeOffset = offset + calleeLeading;
  const { path: fnPath } = parsePath(calleeText, template, calleeOffset);

  const argExpr =
    argText.length === 0
      ? undefined
      : parseExpression(argText, template, offset + parenIdx + 1 + argLeading);

  return {
    type: 'expression',
    raw: trimmed,
    path: fnPath,
    kind: 'functionCall',
    segments: [],
    fn: fnPath,
    arg: argExpr,
  };
}

/**
 * Parse a dot/wildcard path (`a.b[*].c`). Internal helper.
 */
function parsePath(
  text: string,
  template: string,
  offset: number,
): { path: string; segments: PathSegment[] } {
  // Per ADR-12, paths are pure dotted lookups. Intra-expression whitespace
  // is rejected — typos like `{{context tenantId}}` surface as parser
  // errors at publish time rather than silently resolving to `undefined`
  // at runtime. Outer leading/trailing whitespace is stripped by the
  // caller (`parseExpression` applies `raw.trim()`).
  const compact = text;
  const segments: PathSegment[] = [];
  let expectSeparator = false;
  let i = 0;

  while (i < compact.length) {
    const ch = compact[i];

    if (ch === '.') {
      if (!expectSeparator) {
        throw new InterpolationError(
          'Unexpected "." in path',
          template,
          offset + i,
        );
      }
      expectSeparator = false;
      i++;
      continue;
    }

    if (ch === '[') {
      if (!expectSeparator) {
        throw new InterpolationError(
          'Unexpected "[" — a selector must follow an identifier',
          template,
          offset + i,
        );
      }
      if (compact[i + 1] === '*' && compact[i + 2] === ']') {
        segments.push({ type: 'wildcard' });
        i += 3;
        expectSeparator = true;
        continue;
      }
      const quote = compact[i + 1];
      if (quote === '"' || quote === "'") {
        const parsed = readQuotedKey(compact, i + 2, quote);
        if (parsed === undefined || compact[parsed.end] !== ']') {
          throw new InterpolationError(
            'Unterminated quoted key',
            template,
            offset + i,
          );
        }
        segments.push({ type: 'key', name: parsed.name });
        i = parsed.end + 1;
        expectSeparator = true;
        continue;
      }
      throw new InterpolationError(
        'Only the "[*]" wildcard and a quoted key are supported',
        template,
        offset + i,
      );
    }

    if (isIdentifierStart(ch)) {
      let j = i + 1;
      while (j < compact.length && isIdentifierPart(compact[j])) {
        j++;
      }
      const name = compact.slice(i, j);
      segments.push({ type: 'key', name });
      i = j;
      expectSeparator = true;
      continue;
    }

    throw new InterpolationError(
      `Invalid character "${ch}" in path`,
      template,
      offset + i,
    );
  }

  if (!expectSeparator) {
    throw new InterpolationError(
      'Path ends with a trailing separator',
      template,
      offset + compact.length,
    );
  }

  return { path: stringifyPath(segments), segments };
}

function stringifyPath(segments: ReadonlyArray<PathSegment>): string {
  let out = '';
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg === undefined) continue;
    if (seg.type === 'wildcard') {
      out += '[*]';
      continue;
    }
    if (isBareIdentifier(seg.name)) {
      out += i === 0 ? seg.name : `.${seg.name}`;
      continue;
    }
    out += `["${seg.name.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;
  }
  return out;
}

function isBareIdentifier(name: string): boolean {
  if (name.length === 0 || !isIdentifierStart(name[0] ?? '')) return false;
  for (let i = 1; i < name.length; i += 1) {
    if (!isIdentifierPart(name[i])) return false;
  }
  return true;
}

function isIdentifierStart(ch: string): boolean {
  return (
    (ch >= 'a' && ch <= 'z') ||
    (ch >= 'A' && ch <= 'Z') ||
    ch === '_' ||
    ch === '$'
  );
}

function isIdentifierPart(ch: string | undefined): boolean {
  return (
    ch !== undefined &&
    (isIdentifierStart(ch) || (ch >= '0' && ch <= '9') || ch === '-')
  );
}

/**
 * Index just past the closing `quote`, starting at `start` (the first
 * character inside the quotes). A backslash escapes the next character.
 * Returns `undefined` when the quote is never closed.
 */
function skipQuoted(
  text: string,
  start: number,
  quote: string,
): number | undefined {
  let i = start;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '\\') {
      i += 2;
      continue;
    }
    if (ch === quote) return i + 1;
    i++;
  }
  return undefined;
}

function indexOutsideQuotes(text: string, target: string): number {
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '"' || ch === "'") {
      const afterQuote = skipQuoted(text, i + 1, ch);
      if (afterQuote === undefined) return -1;
      i = afterQuote;
      continue;
    }
    if (ch === target) return i;
    i++;
  }
  return -1;
}

/**
 * Replace a `topology.siblings.<id>` key with the id placement minted for it.
 *
 * Only a parsed path segment is rewritten, and only inside `{{…}}`. A minted
 * id that is not a dotted identifier is quoted and escaped. A string that is
 * not a template, or that the parser rejects, is returned unchanged.
 */
export function rewriteTopologySiblingTemplate(
  template: string,
  nodeIdMap: Readonly<Record<string, string>>,
): string {
  let parsed: Template;
  try {
    parsed = parse(template);
  } catch {
    return template;
  }

  let changed = false;
  const parts = parsed.segments.map((segment) => {
    if (segment.type === 'literal') return segment.value;
    const next = rewriteExpression(segment, nodeIdMap);
    if (next === undefined) return `{{${segment.raw}}}`;
    changed = true;
    return `{{${next}}}`;
  });
  return changed ? parts.join('') : template;
}

function rewriteExpression(
  expr: Expression,
  nodeIdMap: Readonly<Record<string, string>>,
): string | undefined {
  if (expr.kind === 'functionCall') {
    const fnNext =
      expr.fn === undefined ? undefined : rewritePathText(expr.fn, nodeIdMap);
    const argNext =
      expr.arg === undefined
        ? undefined
        : rewriteExpression(expr.arg, nodeIdMap);
    if (fnNext === undefined && argNext === undefined) return undefined;
    return `${fnNext ?? expr.fn ?? ''}(${argNext ?? expr.arg?.raw ?? ''})`;
  }

  const next = mapSiblingSegments(expr.segments, nodeIdMap);
  if (next === undefined) return undefined;
  return stringifyPath(next);
}

function rewritePathText(
  path: string,
  nodeIdMap: Readonly<Record<string, string>>,
): string | undefined {
  let parsed: { segments: PathSegment[] };
  try {
    parsed = parsePath(path, path, 0);
  } catch {
    return undefined;
  }
  const next = mapSiblingSegments(parsed.segments, nodeIdMap);
  if (next === undefined) return undefined;
  return stringifyPath(next);
}

function mapSiblingSegments(
  segments: ReadonlyArray<PathSegment>,
  nodeIdMap: Readonly<Record<string, string>>,
): PathSegment[] | undefined {
  let changed = false;
  const next = segments.map((segment, index) => {
    if (segment.type !== 'key' || index !== 2) return segment;
    const root = segments[0];
    const parent = segments[1];
    if (
      root?.type !== 'key' ||
      root.name !== 'topology' ||
      parent?.type !== 'key' ||
      parent.name !== 'siblings'
    ) {
      return segment;
    }
    const minted = nodeIdMap[segment.name];
    if (typeof minted !== 'string' || minted === segment.name) return segment;
    changed = true;
    return { type: 'key' as const, name: minted };
  });
  return changed ? next : undefined;
}

/**
 * Read the inside of `["..."]` or `['...']`. `start` is the first character
 * after the opening quote. A backslash keeps the next character as itself,
 * so an id can contain the quote that wraps it.
 */
function readQuotedKey(
  text: string,
  start: number,
  quote: string,
): { readonly name: string; readonly end: number } | undefined {
  let name = '';
  let i = start;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '\\') {
      const next = text[i + 1];
      if (next === undefined) return undefined;
      name += next;
      i += 2;
      continue;
    }
    if (ch === quote) {
      return { name, end: i + 1 };
    }
    name += ch;
    i++;
  }
  return undefined;
}
